import {
  CopyTradePerformancePeriod,
  CopyTradePeriodPerformance,
  CopyTradePortfolio,
  copyTradePerformancePeriodField,
} from '@flipster/apis/v1/copy-trade/types';
import { isNullable } from '@flipster/utils/nullable';
import { sortingHelper } from '@flipster/utils/sort';

export type CopyTradeSortKey = 'pnl' | 'roi' | 'winRate' | 'copiers' | 'copiersPnl' | 'aum' | 'maxDrawdown' | 'sharpe';

export const getPortfolioPerformance = (
  portfolio: CopyTradePortfolio,
  period: CopyTradePerformancePeriod,
): CopyTradePeriodPerformance | null => {
  const field = copyTradePerformancePeriodField[period];
  return portfolio[field] ?? null;
};

export const filterPortfolios = (portfolios: CopyTradePortfolio[], query: string): CopyTradePortfolio[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return portfolios;

  return portfolios.filter((portfolio) => {
    if (portfolio.nickname.toLowerCase().includes(normalizedQuery)) return true;
    if (portfolio.uid?.toLowerCase().includes(normalizedQuery)) return true;
    if (portfolio.referralCode?.toLowerCase().includes(normalizedQuery)) return true;
    return false;
  });
};

const getSortValue = (
  portfolio: CopyTradePortfolio,
  sortKey: CopyTradeSortKey,
  period: CopyTradePerformancePeriod,
): string | number | null => {
  if (sortKey === 'copiers') return portfolio.currentCopierCount;
  if (sortKey === 'aum') return portfolio.aumUsdt;

  const performance = getPortfolioPerformance(portfolio, period);
  if (!performance) return null;

  switch (sortKey) {
    case 'pnl':
      return performance.pnlUsdt;
    case 'roi':
      return performance.roiPct;
    case 'winRate':
      return performance.winRatePct;
    case 'copiersPnl':
      return performance.copiersPnlUsdt;
    case 'maxDrawdown':
      return performance.maxDrawdownPct;
    case 'sharpe':
      return performance.sharpeRatio;
  }
};

export const sortPortfolios = (
  portfolios: CopyTradePortfolio[],
  sortKey: CopyTradeSortKey,
  period: CopyTradePerformancePeriod,
): CopyTradePortfolio[] => {
  return [...portfolios].sort((a, b) => {
    const valueA = getSortValue(a, sortKey, period);
    const valueB = getSortValue(b, sortKey, period);

    if (isNullable(valueA) && isNullable(valueB)) return 0;
    if (isNullable(valueA)) return 1;
    if (isNullable(valueB)) return -1;

    // Max drawdown is a risk metric — lower is better, so sort asc.
    const order = sortKey === 'maxDrawdown' ? 'asc' : 'desc';

    if (sortKey === 'copiers') {
      return sortingHelper(valueA as number, valueB as number, { type: 'number', order });
    }

    return sortingHelper(String(valueA), String(valueB), { type: 'bignumber', order });
  });
};
